import React, { useState, useEffect } from 'react';
import { Search, Wind, Droplets, Thermometer, Sun, Cloud, CloudRain, CloudSnow, CloudLightning, CloudFog, Sunrise, Sunset, Gauge, AlertCircle, ChevronDown, ChevronUp, Moon } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './components/ui/card.jsx';
import { Alert, AlertDescription } from './components/ui/alert.jsx';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './components/ui/tabs.jsx';
import { Progress } from './components/ui/progress.jsx';
import { Dialog, DialogContent, DialogTrigger } from './components/ui/dialog.jsx';
import axios from 'axios';
import LocationAutocomplete from './components/LocationAutocomplete.jsx';
import tzlookup from 'tz-lookup';

const WeatherDashboard = () => {
  const [cityData, setCityData] = useState(null);
  const [weather, setWeather] = useState(null);
  const [forecast, setForecast] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [unit, setUnit] = useState('F');
  const [expandedCard, setExpandedCard] = useState(null);
  const [isNight, setIsNight] = useState(false);
  const [selectedDay, setSelectedDay] = useState(null);

  useEffect(() => {
    if (weather) {
      const now = new Date();
      const sunrise = new Date(weather.sunrise);
      const sunset = new Date(weather.sunset);
      setIsNight(now < sunrise || now > sunset);
    }
  }, [weather]);

  const fetchWeatherForCity = async (cityName) => {
    setLoading(true);
    setError(null);
    try {
      const weatherResponse = await axios.get(`/api/weather?city=${encodeURIComponent(cityName)}`);
      const forecastResponse = await axios.get(`/api/forecast?city=${encodeURIComponent(cityName)}`);

      // Extract the coordinates from the weather API response
      const { lat, lon } = weatherResponse.data.coord;
      // Determine the IANA timezone (e.g., "Europe/Paris")
      const timeZone = tzlookup(lat, lon);
      
      const currentWeather = {
        temp: Math.round(weatherResponse.data.main.temp),
        humidity: weatherResponse.data.main.humidity,
        windSpeed: Math.round(weatherResponse.data.wind.speed),
        condition: weatherResponse.data.weather[0].main,
        feelsLike: Math.round(weatherResponse.data.main.feels_like),
        sunrise: new Date(weatherResponse.data.sys.sunrise * 1000),
        sunset: new Date(weatherResponse.data.sys.sunset * 1000),
        pressure: weatherResponse.data.main.pressure,
        visibility: weatherResponse.data.visibility,
        description: weatherResponse.data.weather[0].description,
        windDeg: weatherResponse.data.wind.deg,
        timeZone
      };
      const dailyForecasts = forecastResponse.data.list
        .filter((reading) => reading.dt_txt.includes('12:00:00'))
        .slice(0, 5)
        .map((reading) => ({
          day: new Date(reading.dt * 1000).toLocaleDateString('en-US', { weekday: 'short' }),
          temp: Math.round(reading.main.temp),
          condition: reading.weather[0].main,
          pop: Math.round(reading.pop * 100), // Probability of precipitation
          humidity: reading.main.humidity
        }));

      setWeather(currentWeather);
      setForecast(dailyForecasts);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch weather data');
    } finally {
      setLoading(false);
    }
  };

  const convertTemp = (temp) => {
    if (unit === 'C') {
      return Math.round((temp - 32) * 5/9);
    }
    return temp;
  };


  const getWeatherAnimation = (condition) => {
    const baseClasses = "w-12 h-12 transition-all duration-500 ";
    
    switch (condition?.toLowerCase()) {
      case 'clear':
        return isNight 
          ? baseClasses + "text-gray-300 animate-pulse"
          : baseClasses + "text-yellow-400 animate-spin-slow hover:animate-spin";
      case 'clouds':
        return baseClasses + "text-gray-400 hover:translate-x-2 transition-transform";
      case 'rain':
      case 'drizzle':
        return baseClasses + "text-blue-400 animate-bounce hover:animate-none";
      case 'thunderstorm':
        return baseClasses + "text-purple-400 animate-thunder";
      case 'snow':
        return baseClasses + "text-blue-200 animate-float";
      case 'mist':
      case 'fog':
        return baseClasses + "text-gray-400 animate-pulse hover:opacity-50";
      default:
        return baseClasses + "text-yellow-400 animate-spin-slow";
    }
  };

  const getWindDirection = (degrees) => {
    const directions = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
    const index = Math.round(degrees / 45) % 8;
    return directions[index];
  };

  const getWeatherIcon = (condition) => {
    const icon = isNight && condition?.toLowerCase() === 'clear' 
      ? <Moon className={getWeatherAnimation(condition)} />
      : (() => {
          switch (condition?.toLowerCase()) {
            case 'clear':
              return <Sun className={getWeatherAnimation(condition)} />;
            case 'clouds':
              return <Cloud className={getWeatherAnimation(condition)} />;
            case 'rain':
            case 'drizzle':
              return <CloudRain className={getWeatherAnimation(condition)} />;
            case 'thunderstorm':
              return <CloudLightning className={getWeatherAnimation(condition)} />;
            case 'snow':
              return <CloudSnow className={getWeatherAnimation(condition)} />;
            case 'mist':
            case 'fog':
              return <CloudFog className={getWeatherAnimation(condition)} />;
            default:
              return <Sun className={getWeatherAnimation(condition)} />;
          }
        })();
   

    return (
      <div className="relative group">
        {icon}
        <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-black text-white text-xs py-1 px-2 rounded opacity-0 group-hover:opacity-100 transition-opacity">
          {condition}
        </div>
      </div>
    );
  };

  const WeatherCard = ({ day, index }) => (
    <Dialog>
      <DialogTrigger asChild>
        <Card 
          className={`
            bg-white/80 backdrop-blur-sm border-0 shadow-md 
            transition-all duration-300 
            hover:scale-105 hover:shadow-lg 
            cursor-pointer
            ${selectedDay === index ? 'ring-2 ring-blue-400' : ''}
          `}
          onClick={() => setSelectedDay(index)}
        >
          <CardContent className="pt-6">
            <div className="text-center">
              <div className="flex justify-center mb-2">
                {getWeatherIcon(day.condition)}
              </div>
              <h4 className="font-bold text-gray-600">{day.day}</h4>
              <p className="text-2xl font-bold text-blue-600">{convertTemp(day.temp)}°{unit}</p>
              <p className="text-sm text-gray-500">{day.condition}</p>
              <div className="mt-2 text-xs text-gray-400">
                <p>Rain: {day.pop}%</p>
                <p>Humidity: {day.humidity}%</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <div className="space-y-4">
          <h3 className="text-lg font-bold">{day.day} - Detailed Forecast</h3>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <p>Temperature: {convertTemp(day.temp)}°{unit}</p>
              <p>Condition: {day.condition}</p>
              <Progress value={day.pop} className="mt-2" />
              <p>Precipitation: {day.pop}%</p>
            </div>
            <div className="space-y-2">
              <p>Humidity: {day.humidity}%</p>
              <Progress value={day.humidity} className="mt-2" />
              <div className="flex justify-center mt-4">
                {getWeatherIcon(day.condition)}
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );

  return (
    <div className={`
      min-h-screen transition-all duration-1000
      ${isNight 
        ? 'bg-gradient-to-br from-gray-900 to-blue-900 text-white' 
        : 'bg-gradient-to-br from-blue-50 to-blue-100'
      }
      flex items-center p-4 md:p-8
    `}>
      <div className="max-w-4xl w-full mx-auto space-y-8">
        <Card className={`
          backdrop-blur-md shadow-xl border-0 transition-all duration-300 
          ${isNight ? 'bg-gray-800/80 text-white' : 'bg-white/80'}
          hover:shadow-2xl transform hover:-translate-y-1
        `}>
          <CardHeader className="text-center pb-2">
            <CardTitle className={`
              text-3xl md:text-4xl font-bold bg-clip-text text-transparent
              ${isNight 
                ? 'bg-gradient-to-r from-blue-300 to-purple-400' 
                : 'bg-gradient-to-r from-blue-600 to-blue-400'}
            `}>
              Weather Dashboard
            </CardTitle>
            <div className="flex justify-center gap-4 mt-2">
              <button
                onClick={() => setUnit(unit === 'F' ? 'C' : 'F')}
                className={`
                  px-3 py-1 rounded-full transition-colors text-sm font-medium
                  ${isNight 
                    ? 'bg-gray-700 hover:bg-gray-600 text-white' 
                    : 'bg-blue-100 hover:bg-blue-200 text-blue-600'}
                `}
              >
                °{unit} → °{unit === 'F' ? 'C' : 'F'}
              </button>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="text-sm text-gray-500">
              <LocationAutocomplete onSelectCity={(prediction) => {
                const cityName = prediction.description;
                fetchWeatherForCity(cityName);
              }} />
            </div>

            {error && (
              <Alert variant="destructive" className="rounded-xl">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {weather && (
              <Tabs defaultValue="current" className="w-full">
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="current">Current Weather</TabsTrigger>
                  <TabsTrigger value="details">Detailed Info</TabsTrigger>
                </TabsList>

                <TabsContent value="current" className="mt-4">
                  <div className="grid md:grid-cols-2 gap-6">
                    <Card className="overflow-hidden bg-gradient-to-br from-blue-500 to-blue-600 text-white border-0 shadow-lg transform transition-all duration-300 hover:scale-105">
                      <CardContent className="pt-6">
                        <div className="flex items-center justify-center mb-4">
                          {getWeatherIcon(weather.condition)}
                        </div>
                        <div className="text-center">
                          <h3 className="text-8xl font-bold mb-2">{convertTemp(weather.temp)}°{unit}</h3>
                          <p className="text-blue-100 text-4xl italic capitalize">{weather.description}</p>
                        </div>
                      </CardContent>
                    </Card>

                    <Card className="bg-white/80 backdrop-blur-sm shadow-lg border-0">
                      <CardContent className="pt-6">
                        <div className="space-y-6">
                          <div className="flex items-center gap-3 p-3 rounded-lg bg-white/70 transform transition-all duration-300 hover:scale-105">
                            <Wind className="text-blue-500 w-8 h-8" style={{ transform: `rotate(${weather.windDeg}deg)` }} />
                            <div>
                              <p className="text-sm text-gray-500">Wind</p>
                              <p className="text-2xl font-bold text-blue-600">{weather.windSpeed} mph {getWindDirection(weather.windDeg)}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3 p-3 rounded-lg bg-white/70 transform transition-all duration-300 hover:scale-105">
                            <Droplets className="text-blue-500 w-8 h-8" />
                            <div>
                              <p className="text-sm text-gray-500">Humidity</p>
                              <Progress value={weather.humidity} className="mt-2" />
                              <p className="text-2xl font-bold text-blue-600">{weather.humidity}%</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3 p-3 rounded-lg bg-white/70 transform transition-all duration-300 hover:scale-105">
                            <Thermometer className="text-blue-500 w-8 h-8" />
                            <div>
                              <p className="text-sm text-gray-500">Feels Like</p>
                              <p className="text-2xl font-bold text-blue-600">{convertTemp(weather.feelsLike)}°{unit}</p>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </TabsContent>

                <TabsContent value="details" className="mt-4">
                  <Card className="bg-white/80 backdrop-blur-sm shadow-lg border-0">
                    <CardContent className="pt-6">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-4">
                          <div className="flex items-center gap-3">
                            <Sunrise className="text-orange-400 w-6 h-6" />
                            <div>
                              <p className="text-sm text-gray-500">Sunrise</p>
                              <p className="text-2xl font-bold text-blue-600">
                                {weather.sunrise.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', timeZone: weather.timeZone })}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <Gauge className="text-blue-500 w-6 h-6" />
                            <div>
                              <p className="text-sm text-gray-500">Pressure</p>
                              <p className="text-2xl font-bold text-blue-600">{weather.pressure} hPa</p>
                            </div>
                          </div>
                        </div>
                        <div className="space-y-4">
                          <div className="flex items-center gap-3">
                            <Sunset className="text-red-400 w-6 h-6" />
                            <div>
                              <p className="text-sm text-gray-500">Sunset</p>
                              <p className="text-2xl font-bold text-blue-600">
                                {weather.sunset.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', timeZone: weather.timeZone })}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <AlertCircle className="text-blue-500 w-6 h-6" />
                            <div>
                              <p className="text-sm text-gray-500">Visibility</p>
                              <p className="text-2xl font-bold text-blue-600">{(weather.visibility / 1000).toFixed(1)} km</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>
              </Tabs>
            )}

            {forecast && (
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                {forecast.map((day, index) => (
                  <Card 
                    key={index} 
                    className="bg-white/80 backdrop-blur-sm border-0 shadow-md transition-all duration-300 hover:scale-105 hover:shadow-lg"
                  >
                    <CardContent className="pt-6">
                      <div className="text-center">
                        <div className="flex justify-center mb-2">
                          {getWeatherIcon(day.condition)}
                        </div>
                        <h4 className="font-bold text-gray-600">{day.day}</h4>
                        <p className="text-2xl font-bold text-blue-600">{convertTemp(day.temp)}°{unit}</p>
                        <p className="text-sm text-gray-500">{day.condition}</p>
                        <div className="mt-2 text-xs text-gray-400">
                          <p>Rain: {day.pop}%</p>
                          <p>Humidity: {day.humidity}%</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default WeatherDashboard;